import { useEffect, useRef, useState, useCallback } from "react";
import { comparisonRepository } from "../repositories/comparison.repository";
import { shouldMakeAPICall } from "../utils/network";

export const useOfflineComparison = (
  authToken,
  buyerToken,
  searchQuery,
  pageNumber
) => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);
  const abortRef = useRef(null);

  const loadData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    let apiResolved = false;

    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (_) {
        // ignore
      }
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);

    try {
      const canCallAPI = shouldMakeAPICall(authToken, buyerToken);

      // Start API request ASAP (avoid IndexedDB reads delaying network calls)
      const apiPromise = canCallAPI
        ? comparisonRepository.fetchPageFromAPI(
            authToken,
            buyerToken,
            pageNumber,
            searchQuery || "",
            { signal: controller.signal }
          )
        : null;

      // Offline-first: show IndexedDB cache, but don't let it block the API request
      const cachePromise = comparisonRepository
        .getFromCache(buyerToken, searchQuery, pageNumber, 10)
        .then((cached) => {
          if (requestId !== requestIdRef.current) return;
          if (apiResolved) return; // prevent cache overwriting fresher API data
          setData(cached.results);
          setPagination({
            page_count: cached.page_count,
            current_page_count: 10,
          });
        })
        .catch(() => {
          // ignore cache errors
        });

      if (apiPromise) {
        const { history, pagination: apiPagination } = await apiPromise;
        apiResolved = true;

        // Update UI from API response (online mode)
        if (requestId === requestIdRef.current) {
          setData(Array.isArray(history) ? history : []);
          setPagination({
            page_count: apiPagination.page_count || 1,
            current_page_count: apiPagination.current_page_count || 10,
          });
        }

        // Store current page in IndexedDB (offline cache) in the background
        void comparisonRepository
          .savePageToCache(history, pageNumber, buyerToken)
          .catch(() => {});

        // Background sync shouldn't block first paint / first API call
        void (async () => {
          try {
            const status = await comparisonRepository.getSyncStatus(buyerToken);
            if (status.cacheComplete && status.cacheCount > 0) return;

            let basePageCount = apiPagination.page_count || 1;
            let baseTotalCount = apiPagination.total_count || 0;

            // When search is applied, fetch unfiltered pagination once for full offline cache.
            if (searchQuery) {
              try {
                const base = await comparisonRepository.fetchPageFromAPI(
                  authToken,
                  buyerToken,
                  1,
                  ""
                );
                basePageCount = base.pagination.page_count || basePageCount;
                baseTotalCount = base.pagination.total_count || baseTotalCount;
                await comparisonRepository.savePageToCache(
                  base.history,
                  1,
                  buyerToken
                );
              } catch (_) {
                // ignore; background sync will proceed with best-known values
              }
            }

            await comparisonRepository.syncRemainingPagesInBackground(
              authToken,
              buyerToken,
              basePageCount,
              pageNumber,
              { expectedTotal: baseTotalCount }
            );
          } catch (_) {
            // ignore background sync errors
          }
        })();
      } else {
        // No API call possible (offline/not authenticated): wait for cache and finish.
        await cachePromise;
      }

      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    } catch (error) {
      const aborted =
        error?.name === "CanceledError" ||
        error?.code === "ERR_CANCELED" ||
        error?.message?.toLowerCase?.().includes("canceled") ||
        error?.message?.toLowerCase?.().includes("cancelled");

      if (!aborted) {
        console.warn("Comparison history fetch failed:", error?.message || error);
      }

      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [authToken, buyerToken, searchQuery, pageNumber]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch (_) {
          // ignore
        }
      }
    };
  }, []);

  return { data, pagination, loading, refetch: loadData };
};
